// Foundation invariant for #169: scenes hold references to assets, never
// the assets themselves. After Phase 2B (#394 + #395) the webapp produces
// URL-shaped scenes from the moment of drop. This validator catches any
// regression (or unusual path that still produces base64) at save time.
//
// ENFORCE mode (#528): every raster/video dropzone is now upload-first
// (BgSelector, logos, template uploads, custom QR, and per-template
// CustomBgUpload), so a fresh scene never produces an inline raster/video
// blob. Inline blobs now mean a legacy scene (saved before upload-first) —
// blocked with a user-visible "reload and re-upload" dialog rather than
// silently shipping a multi-megabyte JSON.
//
// SVG data URIs are EXEMPT: they're vector, tiny, and intentionally inline so
// the gradient editor can round-trip the markup (see decodeSvgFromDataUrl /
// the SVG paths in BgSelector + CustomBgUpload). They are not the payload
// problem the ban targets, so they never block a save.

import {introspectScenePayload} from "./sceneIntrospection";

// Toggle for the enforce behavior. `true` blocks saves that carry inline
// raster/video base64; tests parameterize directly via the options arg.
const ENFORCE_NO_INLINE_BLOBS = true;

// Media types that are allowed to stay inline (never counted as blocking).
const EXEMPT_MEDIA_TYPES = new Set(["image/svg+xml"]);

/**
 * Inspect a scene about to be saved and decide whether to allow it.
 *
 * Returns `{ok: true}` for clean scenes (no `data:` URIs anywhere). For
 * scenes with inline blobs, returns the introspection details plus an
 * `ok` field reflecting the current mode: `true` in shadow mode (warn
 * but allow), `false` in enforce mode (block).
 *
 * The caller is responsible for logging / emitting telemetry / showing
 * dialogs — this function just decides yes/no and surfaces the data.
 */
export function checkSceneForInlineBlobs(scene, {enforce = ENFORCE_NO_INLINE_BLOBS} = {}) {
  const {blobs, payloadBytes} = introspectScenePayload(scene);
  // Only raster/video inline blobs block; intentional inline SVG is exempt.
  const blocking = blobs.filter((b) => !EXEMPT_MEDIA_TYPES.has(b.mediatype));
  if (blocking.length === 0) {
    return {ok: true, inlineBlobCount: 0};
  }
  const inlineBlobBytes = blocking.reduce((sum, b) => sum + b.bytes, 0);
  return {
    ok: !enforce,
    enforceMode: enforce,
    inlineBlobCount: blocking.length,
    inlineBlobBytes,
    payloadBytes,
    // Top-5 offenders only — keeps the telemetry payload bounded and the
    // diagnostics dump paste-friendly. Total counts above still reflect
    // the full blocking set.
    topFields: blocking.slice(0, 5).map((b) => ({path: b.path, bytes: b.bytes})),
  };
}
